import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { EnterIcon, ImageIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { Card, LocationPin } from '@producer/ui-kit';

import { CoverImage } from '../../../components/cover-image/cover-image';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { formatAddress } from '../../../core/services/address.service';
import { selectFiles } from '../../../core/services/file.service';
import { formatPicture } from '../../../core/services/image.service';
import { COVER_ACCEPT, COVER_SIZE } from '../../../components/cover-image/cover-format';
import { updateLocationCover } from '../location.slice';
import { Location } from '../../../app/entities/location';
import { FormBuilderModal } from '../../../components/form-builder-modal/form-builder-modal';
import { buildLocationForm } from './location-form.definition';

import type { Frame } from '../../../lib/utils/transform-image';
import { openModal } from '../../../core/modal/open-modal';

interface LocationCardProps {
  location: Location;
}

export const LocationCard: React.FC<LocationCardProps> = observer(({ location }) => {
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updateModalKey, setUpdateModalKey] = useState(location?._id);
  const { t } = useTranslation('location');

  useEffect(() => {
    if (location?._id) {
      // To rerender the update form modal
      setUpdateModalKey(location._id + Math.random());
    }
  }, [location?._id]);

  const handleDeleteLocation = async () => {
    await location.delete();
  };

  const handleEditLocation = async (values: Partial<Location>) => {
    await location.update(values);

    // To rerender the update form modal
    setUpdateModalKey(location._id + Math.random());
  };

  const handleSubmitCover = async (frame: Frame) => {
    updateLocationCover({
      locationId: location._id,
      file: frame,
    });
  };

  const goToLocation = async () => {
    const href = `./${location._id}`;
    navigate(href);
  };

  const handleOpenEditModal = async () => {
    setIsEditModalOpen(true);
  };

  /** The way in for changing a cover: the empty state is inert, so the menu owns it. */
  const handleEditCover = async () => {
    const files = await selectFiles({ accept: COVER_ACCEPT });

    if (!files?.length) {
      return;
    }

    const frame = await formatPicture(files[0], COVER_SIZE);
    await handleSubmitCover(frame);
  };

  const menuItems = [
    {
      title: 'Open',
      icon: <EnterIcon />,
      onSelect: () => navigate(`./${location._id}`),
    },
    {
      title: 'Edit',
      icon: <Pencil1Icon />,
      onSelect: handleOpenEditModal,
    },
    {
      title: 'Edit cover\u2026',
      icon: <ImageIcon />,
      onSelect: handleEditCover,
    },
    { separator: true as const },
    {
      title: 'Delete',
      icon: <TrashIcon />,
      type: 'danger' as const,
      onSelect: () =>
        openModal(DeleteDialog, {
          title: 'Delete location ?',
          text: `Are you sure you want to delete ${location.name}?`,
          onSubmit: handleDeleteLocation,
        }),
    },
  ];

  const updateLocationFormDefinition = buildLocationForm(false, t);

  return (
    <>
      <Card
        media={
          <CoverImage
            src={location.cover?.src || ''}
            title={location.name}
            onEdit={handleSubmitCover}
            onDoubleClick={goToLocation}
            placeholderIcon={<LocationPin />}
          />
        }
        title={location.name}
        subtitle={location.address ? formatAddress(location.address) : undefined}
        menu={menuItems}
        onClick={goToLocation}
      />

      {isEditModalOpen && (
        <FormBuilderModal
          key={updateModalKey}
          model="location"
          definition={updateLocationFormDefinition}
          initialValues={location}
          onCancel={() => setIsEditModalOpen(false)}
          onSubmit={handleEditLocation}
        />
      )}
    </>
  );
});
